import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  ConfigProvider,
  DatePicker,
  Form,
  Input,
  Modal,
  Select,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
} from "antd";
import {
  BarChartOutlined,
  DeleteOutlined,
  DollarCircleOutlined,
  DownloadOutlined,
  EyeOutlined,
  FileTextOutlined,
  InboxOutlined,
  TruckOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import IntlCurrencyInput from "react-intl-currency-input";
import freightApi2 from "../../../service/modules/freight.api";
import freightFinancialService, {
  FINANCIAL_EDIT_LOCKED_MESSAGE,
  isFinancialRecordEditable,
  validateBoletoFile,
} from "../../../service/freight-financial.service";
import shipperService from "../../../service/shipper.service";
import {
  sendFreightInvoiceBoletoFromPanel,
  sendFreightInvoiceChargeFromPanel,
} from "../freightInvoiceEmailHandlers";
import { resolvePaymentConditionDescription } from "../../../util/freight/payment-due-date";
import { buildFinancialInstallmentsForFreight, mergeInstallmentUpdate, normalizeBoletoAttachment, resolveInstallmentBoletoAttachment } from "../../../util/freight/freight-financial-installments";
import {
  buildFreightInvoicePendingDocumentsMessage,
  validateRequiredFreightDocumentsForBoletoSend,
} from "../../../util/freight/freight-document-utils";
import {
  FINANCIAL_STATUS_OPTIONS,
  formatFinancialStatusValue,
  hasBoletoAttachmentOnRecord,
  normalizeFinancialStatusValue,
  normalizeStoredFinancialStatusValue,
} from "../../../util/freight/freight-v2-table-utils";
import theme from "../../../theme";
import "./FreightFinancialEditModal.css";

const { Dragger } = Upload;
const { Text } = Typography;

const currencyConfig = {
  locale: "pt-BR",
  formats: {
    number: {
      BRL: {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
    },
  },
};

const antdModalTheme = {
  token: {
    colorPrimary: theme.colors.primary,
    colorError: theme.colors.highlight,
    colorText: theme.colors.textPrimary,
    colorTextSecondary: theme.colors.textSecondary,
    colorBorder: theme.colors.primaryBorder,
    borderRadius: theme.radius.small,
    fontFamily: theme.typography.fontFamily,
  },
  components: {
    Button: {
      borderRadius: theme.radius.small,
      controlHeightLG: 40,
    },
    DatePicker: {
      borderRadius: theme.radius.small,
      controlHeightLG: 40,
    },
    Input: {
      borderRadius: theme.radius.small,
      controlHeightLG: 40,
    },
    Select: {
      borderRadius: theme.radius.small,
      controlHeightLG: 40,
    },
    Upload: {
      colorBorder: theme.colors.primaryBorder,
    },
  },
};

const modalStyles = {
  content: {
    padding: 0,
    overflow: "hidden",
    borderRadius: theme.radius.large,
    background: theme.colors.surface,
    boxShadow: theme.elevation.base,
  },
  header: {
    margin: 0,
    padding: `${theme.spacing.md}px ${theme.spacing.lg}px`,
    borderBottom: `1px solid ${theme.colors.primaryBorder}`,
    background: theme.colors.surface,
  },
  body: {
    maxHeight: "calc(72vh - 72px)",
    overflowY: "auto",
    padding: `${theme.spacing.md}px ${theme.spacing.lg}px`,
    background: theme.colors.background,
  },
  footer: {
    margin: 0,
    padding: `${theme.spacing.sm}px ${theme.spacing.lg}px`,
    borderTop: `1px solid ${theme.colors.primaryBorder}`,
    background: theme.colors.surface,
    position: "sticky",
    bottom: 0,
    zIndex: 2,
  },
};

const controlStyle = {
  width: "100%",
  borderRadius: theme.radius.small,
};

const currencyInputStyle = {
  width: "100%",
  height: 40,
  borderRadius: theme.radius.small,
  border: `1px solid ${theme.colors.primaryBorder}`,
  color: theme.colors.textPrimary,
  fontFamily: theme.typography.fontFamily,
  fontSize: 16,
  padding: "6px 11px",
  boxShadow: "none",
};

const currencyInputDisabledStyle = {
  ...currencyInputStyle,
  background: "rgba(30, 33, 36, 0.04)",
  color: theme.colors.textSecondary,
  cursor: "not-allowed",
};

function SectionCard({ icon, title, children }) {
  return (
    <section className="financial-modal-card">
      <div className="financial-modal-card__header">
        <span className="financial-modal-card__icon">{icon}</span>
        <h3 className="financial-modal-card__title">{title}</h3>
      </div>
      <div className="financial-modal-card__body">{children}</div>
    </section>
  );
}

function ModalHeader({ installmentLabel, numberSerial, shipperName, readOnly }) {
  return (
    <div className="financial-modal-header">
      <h2 className="financial-modal-header__title">
        {readOnly
          ? "Visualização de Registro Financeiro"
          : "Edição de Registro Financeiro"}
        {installmentLabel ? ` — Parcela ${installmentLabel}` : ""}
      </h2>
      <p className="financial-modal-header__meta">
        Frete {numberSerial || "—"} · {shipperName || "—"}
      </p>
    </div>
  );
}

function HighlightField({ label, value, emphasis = false, multiline = false }) {
  return (
    <div
      className={`financial-highlight-field${
        emphasis ? " financial-highlight-field--emphasis" : ""
      }`}
    >
      <span className="financial-highlight-field__label">{label}</span>
      <span
        className={`financial-highlight-field__value${
          multiline ? " financial-highlight-field__value--multiline" : ""
        }`}
        title={value}
      >
        {value}
      </span>
    </div>
  );
}

function toDayjs(value) {
  if (!value) return null;
  if (dayjs.isDayjs(value)) return value;

  if (typeof value?.toDate === "function") {
    const parsed = dayjs(value.toDate());
    return parsed.isValid() ? parsed : null;
  }

  if (
    typeof value === "object" &&
    Number.isFinite(value.seconds) &&
    Number.isFinite(value.nanoseconds)
  ) {
    const parsed = dayjs(value.seconds * 1000);
    return parsed.isValid() ? parsed : null;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    const brDateMatch = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(trimmed);

    if (brDateMatch) {
      const [, day, month, year] = brDateMatch;
      const parsed = dayjs(`${year}-${month}-${day}`);
      return parsed.isValid() ? parsed : null;
    }
  }

  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
}

function formatDatePayload(value) {
  if (!value) return null;
  return dayjs(value).format("YYYY-MM-DD");
}

function normalizeDateOptionValue(value) {
  if (!value) return null;

  if (
    !dayjs.isDayjs(value) &&
    typeof value === "object" &&
    typeof value?.toDate !== "function" &&
    !Number.isFinite(value?.seconds)
  ) {
    return normalizeDateOptionValue(
      value.dueDate ??
        value.date ??
        value.dataVencimento ??
        value.data ??
        value.vencimento ??
        value.value,
    );
  }

  const parsed = toDayjs(value);
  return parsed ? formatDatePayload(parsed) : null;
}

function buildDueDateOptions(...sources) {
  const optionsByValue = new Map();

  sources.forEach((source) => {
    if (!Array.isArray(source)) return;

    source.forEach((item) => {
      const value = normalizeDateOptionValue(item);
      if (!value || optionsByValue.has(value)) return;

      optionsByValue.set(value, {
        value,
        label: `Parcela ${optionsByValue.size + 1} - ${dayjs(value).format(
          "DD/MM/YYYY",
        )}`,
      });
    });
  });

  return Array.from(optionsByValue.values());
}

function firstValidDate(...values) {
  for (const value of values) {
    const parsed = toDayjs(value);
    if (parsed) return parsed;
  }

  return null;
}

function getUploadedBoletoAttachment(uploadResult, fallbackFile) {
  const payload = uploadResult?.data || {};
  const attachment =
    payload?.boletoAttachment ||
    payload?.attachment ||
    payload?.file ||
    (payload?.name || payload?.url ? payload : null);

  if (attachment) return normalizeBoletoAttachment(attachment);

  return fallbackFile
    ? {
        name: fallbackFile.name,
        size: fallbackFile.size,
        type: fallbackFile.type,
      }
    : null;
}

function resolveBoletoUrl(attachment, localFile) {
  if (localFile) return URL.createObjectURL(localFile);
  if (!attachment) return "";
  if (typeof attachment === "string") return attachment.trim();
  return String(attachment.url || attachment.downloadUrl || "").trim();
}

function resolveBoletoFileName(attachment, localFile) {
  if (localFile?.name) return localFile.name;
  if (!attachment) return "boleto";
  if (typeof attachment === "string") {
    const fromUrl = attachment.split("?")[0].split("/").pop();
    return fromUrl ? decodeURIComponent(fromUrl) : "boleto";
  }
  return attachment.name || attachment.originalName || attachment.fileName || "boleto";
}

function formatBoletoMeta(attachment, isLocalFile) {
  if (isLocalFile) {
    return "Arquivo selecionado — será enviado ao salvar ou ao disparar o envio";
  }

  const dateValue =
    attachment?.uploadedAt ||
    attachment?.createdAt ||
    attachment?.updatedAt ||
    attachment?.date;

  const parsed = toDayjs(dateValue);
  if (parsed) {
    return `Anexado em ${parsed.format("DD/MM/YYYY HH:mm")}`;
  }

  return "Boleto anexado ao registro";
}

function BoletoAttachmentBlock({
  attachment,
  localFile,
  statusLabel,
  canRemove,
  removing,
  onView,
  onDownload,
  onRemove,
}) {
  const fileName = resolveBoletoFileName(attachment, localFile);
  const meta = formatBoletoMeta(attachment, Boolean(localFile));
  const canOpen = Boolean(resolveBoletoUrl(attachment, localFile));

  return (
    <div className="financial-boleto-file">
      <div className="financial-boleto-file__main">
        <FileTextOutlined className="financial-boleto-file__icon" />
        <div className="financial-boleto-file__info">
          <Text className="financial-boleto-file__name">{fileName}</Text>
          <Text className="financial-boleto-file__meta">{meta}</Text>
          {statusLabel ? (
            <Tag color="blue" style={{ marginTop: 6 }}>
              {statusLabel}
            </Tag>
          ) : null}
        </div>
      </div>
      <div className="financial-boleto-file__actions">
        {canOpen ? (
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={onView}
            disabled={!canOpen}
          >
            Visualizar
          </Button>
        ) : null}
        {canOpen ? (
          <Button
            size="small"
            icon={<DownloadOutlined />}
            onClick={onDownload}
            disabled={!canOpen}
          >
            Baixar
          </Button>
        ) : null}
        {canRemove ? (
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={onRemove}
            loading={removing}
          >
            Remover
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function FreightFinancialEditModal({
  open,
  record,
  onClose,
  onSaved,
  canEditFinancial = false,
  canManageBoleto = false,
}) {
  const freightId = record?.freightId || record?.id;
  const installmentLabel = record?.installmentLabel;
  const [form] = Form.useForm();
  const initialSnapshotRef = useRef("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingBoleto, setSendingBoleto] = useState(false);
  const [charging, setCharging] = useState(false);
  const [removingBoleto, setRemovingBoleto] = useState(false);
  const [boletoFile, setBoletoFile] = useState(null);
  const [boletoAttachment, setBoletoAttachment] = useState(null);
  const [freightData, setFreightData] = useState(null);
  const [shipperData, setShipperData] = useState(null);
  const user = useSelector((state) => state.user);
  const [currentFinancialStatus, setCurrentFinancialStatus] =
    useState("pendente");
  const [dueDateOptions, setDueDateOptions] = useState([]);
  const [financialInstallments, setFinancialInstallments] = useState([]);
  const [readOnlyData, setReadOnlyData] = useState({
    numberSerial: "",
    shipperName: "",
    paymentConditions: "",
    shipperObservation: "",
    internalObservations: "",
  });

  const status = Form.useWatch("financialStatus", form);
  const dueDateWatch = Form.useWatch("dueDate", form);
  const loadingDateWatch = Form.useWatch("loadingDate", form);
  const settlementDateWatch = Form.useWatch("settlementDate", form);
  const valueWatch = Form.useWatch("value", form);
  const normalizedStatus = normalizeFinancialStatusValue(status);
  const isPaid = normalizedStatus === "pago";
  const isFinancialEditLocked =
    !isFinancialRecordEditable(currentFinancialStatus);
  const isBusy = saving || sendingBoleto || charging || removingBoleto;
  const boletoActionsDisabled =
    loading || isBusy || !freightId || !canManageBoleto;
  const financialFieldsDisabled =
    loading || saving || isFinancialEditLocked || !canEditFinancial;
  const hasBoletoAttached =
    hasBoletoAttachmentOnRecord({ boletoAttachment }) || Boolean(boletoFile);

  const isFormDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;

    const values = form.getFieldsValue(true);
    const currentSnapshot = JSON.stringify({
      ...values,
      loadingDate: formatDatePayload(values.loadingDate),
      dueDate: formatDatePayload(values.dueDate),
      settlementDate: formatDatePayload(values.settlementDate),
      boletoFileName: boletoFile?.name || boletoAttachment?.name || null,
    });

    return currentSnapshot !== initialSnapshotRef.current;
  }, [
    boletoAttachment,
    boletoFile,
    dueDateWatch,
    form,
    loadingDateWatch,
    settlementDateWatch,
    status,
    valueWatch,
  ]);

  const saveButtonState = useMemo(() => {
    if (!canEditFinancial) {
      return {
        enabled: false,
        reason: "Você não possui permissão para editar este registro.",
      };
    }

    if (loading) {
      return {
        enabled: false,
        reason: "Aguarde o carregamento dos dados.",
      };
    }

    if (isBusy) {
      return {
        enabled: false,
        reason: "Aguarde a conclusão da operação em andamento.",
      };
    }

    if (!freightId) {
      return { enabled: false, reason: "Frete não identificado." };
    }

    if (isFinancialEditLocked) {
      return {
        enabled: false,
        reason:
          "Este registro não pode ser alterado devido ao status atual.",
      };
    }

    const hasRequiredFields =
      Boolean(status) &&
      Boolean(loadingDateWatch) &&
      Boolean(dueDateWatch) &&
      Number(valueWatch ?? 0) > 0 &&
      (!isPaid || Boolean(settlementDateWatch));

    if (!hasRequiredFields) {
      return {
        enabled: false,
        reason: "Preencha os campos obrigatórios para salvar.",
      };
    }

    if (!isFormDirty) {
      return {
        enabled: false,
        reason: "Não há alterações para salvar.",
      };
    }

    return { enabled: true, reason: "" };
  }, [
    canEditFinancial,
    dueDateWatch,
    freightId,
    isBusy,
    isFinancialEditLocked,
    isFormDirty,
    isPaid,
    loading,
    loadingDateWatch,
    settlementDateWatch,
    status,
    valueWatch,
  ]);

  const sendBoletoState = useMemo(() => {
    if (!canManageBoleto) {
      return {
        enabled: false,
        reason: "Você não possui permissão para enviar boletos.",
      };
    }

    if (loading || sendingBoleto || charging || removingBoleto) {
      return {
        enabled: false,
        reason: "Aguarde a conclusão da operação em andamento.",
      };
    }

    if (!freightId) {
      return { enabled: false, reason: "Frete não identificado." };
    }

    if (!hasBoletoAttached) {
      return {
        enabled: false,
        reason: "Anexe um boleto antes de enviar.",
      };
    }

    if (!isFinancialRecordEditable(currentFinancialStatus)) {
      return {
        enabled: false,
        reason: FINANCIAL_EDIT_LOCKED_MESSAGE,
      };
    }

    const attachmentForValidation = boletoFile
      ? { name: boletoFile.name, url: "local" }
      : boletoAttachment;

    const validation = validateRequiredFreightDocumentsForBoletoSend(
      freightData,
      attachmentForValidation,
    );

    if (!validation.valid) {
      return {
        enabled: false,
        reason: buildFreightInvoicePendingDocumentsMessage(validation),
      };
    }

    return { enabled: true, reason: "" };
  }, [
    boletoAttachment,
    boletoFile,
    canManageBoleto,
    charging,
    currentFinancialStatus,
    freightData,
    freightId,
    hasBoletoAttached,
    loading,
    removingBoleto,
    sendingBoleto,
  ]);

  const resetState = useCallback(() => {
    form.resetFields();
    setBoletoFile(null);
    setBoletoAttachment(null);
    setFreightData(null);
    setShipperData(null);
    setCurrentFinancialStatus("pendente");
    setDueDateOptions([]);
    setFinancialInstallments([]);
    initialSnapshotRef.current = "";
    setReadOnlyData({
      numberSerial: "",
      shipperName: "",
      paymentConditions: "",
      shipperObservation: "",
      internalObservations: "",
    });
  }, [form]);

  const buildSnapshot = useCallback(() => {
    const values = form.getFieldsValue(true);
    return JSON.stringify({
      ...values,
      loadingDate: formatDatePayload(values.loadingDate),
      dueDate: formatDatePayload(values.dueDate),
      settlementDate: formatDatePayload(values.settlementDate),
      boletoFileName: boletoFile?.name || boletoAttachment?.name || null,
    });
  }, [boletoAttachment, boletoFile, form]);

  const isDirty = useCallback(() => {
    if (!initialSnapshotRef.current) return false;
    return buildSnapshot() !== initialSnapshotRef.current;
  }, [buildSnapshot]);

  const updateInitialBoletoSnapshot = useCallback((fileName) => {
    if (!initialSnapshotRef.current) return;

    try {
      const snapshot = JSON.parse(initialSnapshotRef.current);
      initialSnapshotRef.current = JSON.stringify({
        ...snapshot,
        boletoFileName: fileName || null,
      });
    } catch {
      initialSnapshotRef.current = "";
    }
  }, []);

  const loadModalData = useCallback(async () => {
    if (!freightId) return;

    setLoading(true);
    try {
      const [financialRecord, freightResult, shipperResult] = await Promise.all([
        freightFinancialService.getFinancialRecord(record),
        freightApi2.getById(freightId).catch(() => null),
        record?.shipper?.uid
          ? shipperService
              .getShipperAvailable(record.shipper.uid)
              .catch(() => null)
          : Promise.resolve(null),
      ]);

      const freightPayload =
        freightResult?.data?.data ?? freightResult?.data ?? freightResult ?? {};
      const shipperPayload = shipperResult?.exists ? shipperResult.data() : null;
      setFreightData(freightPayload);
      setShipperData(shipperPayload);

      const paymentConditions =
        resolvePaymentConditionDescription(shipperPayload?.financial) ||
        record?.paymentConditionLabel ||
        "—";

      const shipperObservation = shipperPayload?.financial?.observation || "—";
      const internalObservations =
        freightPayload?.freight?.internalObservations || "—";

      setReadOnlyData({
        numberSerial: record?.numberSerial || "—",
        shipperName: record?.shipper?.name || "—",
        paymentConditions,
        shipperObservation,
        internalObservations,
      });

      const loadedInstallments =
        Array.isArray(financialRecord?.financialInstallments) &&
        financialRecord.financialInstallments.length > 0
          ? financialRecord.financialInstallments
          : buildFinancialInstallmentsForFreight({
              ...record,
              ...financialRecord,
              financialInstallments:
                financialRecord?.financialInstallments ??
                record?.financialInstallments ??
                [],
            });
      setFinancialInstallments(loadedInstallments);

      const installmentNumber = record?.installmentNumber ?? 1;
      const existingBoleto = resolveInstallmentBoletoAttachment({
        financialInstallments: loadedInstallments,
        installmentNumber,
        rootBoletoAttachment:
          financialRecord?.boletoAttachment ?? record?.boletoAttachment ?? null,
      });
      setBoletoAttachment(existingBoleto);

      const financialStatus =
        normalizeStoredFinancialStatusValue(
          financialRecord?.statusFinancial ?? financialRecord?.financialStatus,
          "pendente",
        ) || "pendente";
      setCurrentFinancialStatus(financialStatus);

      if (!isFinancialRecordEditable(financialStatus)) {
        message.warning(FINANCIAL_EDIT_LOCKED_MESSAGE);
      }

      const nextDueDateOptions = buildDueDateOptions(
        financialRecord?.dueDateInstallments,
        financialRecord?.dueDates,
        financialRecord?.dueDateOptions,
        record?.dueDateInstallments,
        record?.dueDates,
        freightPayload?.dueDateInstallments,
        freightPayload?.dueDates,
        freightPayload?.dueDateOptions,
        freightPayload?.freight?.dueDateInstallments,
        freightPayload?.freight?.dueDates,
        freightPayload?.freight?.dueDateOptions,
      );
      setDueDateOptions(nextDueDateOptions);

      const formValues = {
        financialStatus,
        loadingDate: toDayjs(
          financialRecord?.loadingDate || record?.firstDelivery?.dateColeta,
        ),
        dueDate: firstValidDate(
          financialRecord?.dueDate,
          record?.dueDate,
          freightPayload?.dueDate,
          freightPayload?.freight?.dueDate,
          nextDueDateOptions[0]?.value,
        ),
        value: Number(
          financialRecord?.value ?? record?.freight?.valueFreightage ?? 0,
        ),
        settlementDate: toDayjs(
          financialRecord?.settlementDate ||
            financialRecord?.dataBaixa ||
            record?.settlementDate ||
            record?.dataBaixa,
        ),
      };

      form.setFieldsValue(formValues);

      requestAnimationFrame(() => {
        initialSnapshotRef.current = JSON.stringify({
          ...formValues,
          loadingDate: formatDatePayload(formValues.loadingDate),
          dueDate: formatDatePayload(formValues.dueDate),
          settlementDate: formatDatePayload(formValues.settlementDate),
          boletoFileName: existingBoleto?.name || null,
        });
      });
    } catch (error) {
      console.error("Erro ao carregar dados financeiros:", error);
      message.error("Não foi possível carregar os dados financeiros.");
    } finally {
      setLoading(false);
    }
  }, [form, freightId, record]);

  useEffect(() => {
    if (open && freightId) {
      loadModalData();
    } else if (!open) {
      resetState();
    }
  }, [open, freightId, loadModalData, resetState]);

  useEffect(() => {
    if (isFinancialEditLocked) return;
    if (!isPaid) {
      form.setFieldsValue({ settlementDate: null });
      form.setFields([{ name: "settlementDate", errors: [] }]);
    }
  }, [isPaid, isFinancialEditLocked, form]);

  const handleAttemptClose = useCallback(() => {
    if (isBusy) return;

    if (isDirty()) {
      Modal.confirm({
        title: "Alterações não salvas",
        content: "Deseja sair sem salvar as alterações?",
        okText: "Sair sem salvar",
        cancelText: "Continuar editando",
        onOk: () => {
          resetState();
          onClose?.();
        },
      });
      return;
    }

    resetState();
    onClose?.();
  }, [isBusy, isDirty, onClose, resetState]);

  const handleBoletoSelect = (file) => {
    if (isFinancialEditLocked) {
      message.warning(FINANCIAL_EDIT_LOCKED_MESSAGE);
      return Upload.LIST_IGNORE;
    }

    const validation = validateBoletoFile(file);
    if (!validation.valid) {
      message.error(validation.error);
      return Upload.LIST_IGNORE;
    }

    setBoletoFile(file);
    setBoletoAttachment({ name: file.name, size: file.size });
    return false;
  };

  const handleViewBoleto = () => {
    const url = resolveBoletoUrl(boletoAttachment, boletoFile);
    if (!url) {
      message.warning("Não foi possível abrir o boleto.");
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleDownloadBoleto = () => {
    const url = resolveBoletoUrl(boletoAttachment, boletoFile);
    if (!url) {
      message.warning("Não foi possível baixar o boleto.");
      return;
    }

    const link = window.document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.download = resolveBoletoFileName(boletoAttachment, boletoFile);
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
  };

  const handleRemoveBoleto = async () => {
    if (isFinancialEditLocked) {
      message.warning(FINANCIAL_EDIT_LOCKED_MESSAGE);
      return;
    }

    if (boletoFile) {
      setBoletoFile(null);
      setBoletoAttachment(null);
      return;
    }

    if (!freightId || !boletoAttachment) {
      setBoletoAttachment(null);
      return;
    }

    try {
      setRemovingBoleto(true);
      const installmentNumber = record?.installmentNumber ?? 1;
      const removeResult = await freightFinancialService.removeBoleto(freightId, {
        installmentNumber,
        financialInstallments,
      });
      const responseInstallments = removeResult?.data?.financialInstallments;
      if (Array.isArray(responseInstallments) && responseInstallments.length > 0) {
        setFinancialInstallments(responseInstallments);
      } else {
        setFinancialInstallments((current) =>
          mergeInstallmentUpdate(current, installmentNumber, {
            boletoAttachment: null,
          }),
        );
      }
      setBoletoAttachment(null);
      updateInitialBoletoSnapshot(null);
      message.success("Boleto removido com sucesso.");
    } catch (error) {
      console.error("Erro ao remover boleto:", error);
      message.error(error?.message || "Não foi possível remover o boleto.");
    } finally {
      setRemovingBoleto(false);
    }
  };

  const handleSave = async () => {
    if (isFinancialEditLocked) {
      message.warning(FINANCIAL_EDIT_LOCKED_MESSAGE);
      return;
    }

    try {
      const values = await form.validateFields();
      setSaving(true);

      const statusFinancial =
        normalizeFinancialStatusValue(values.financialStatus);
      if (!statusFinancial) {
        message.error(
          "Status financeiro inválido. Use Pendente, Emitido, Pago, Vencido ou Cancelado.",
        );
        return;
      }

      const installmentNumber = record?.installmentNumber ?? 1;
      let uploadedAttachment = boletoAttachment;
      let updatedInstallments = financialInstallments;

      if (boletoFile && freightId) {
        const uploadResult = await freightFinancialService.uploadBoleto(
          freightId,
          boletoFile,
          {
            installmentNumber,
            financialInstallments,
          },
        );
        uploadedAttachment = getUploadedBoletoAttachment(uploadResult, boletoFile);
        const responseInstallments = uploadResult?.data?.financialInstallments;
        if (
          Array.isArray(responseInstallments) &&
          responseInstallments.length > 0
        ) {
          updatedInstallments = responseInstallments;
        } else if (uploadedAttachment) {
          updatedInstallments = mergeInstallmentUpdate(
            financialInstallments,
            installmentNumber,
            { boletoAttachment: uploadedAttachment },
          );
        }
      }

      const settlementDate = formatDatePayload(values.settlementDate);
      const payload = {
        statusFinancial,
        financialStatus: formatFinancialStatusValue(statusFinancial),
        loadingDate: formatDatePayload(values.loadingDate),
        dueDate: formatDatePayload(values.dueDate),
        value: Number(values.value ?? 0),
        settlementDate: statusFinancial === "pago" ? settlementDate : null,
        dataBaixa: statusFinancial === "pago" ? settlementDate : null,
        installmentNumber,
        installmentTotal: record?.installmentTotal ?? 1,
        financialInstallments: mergeInstallmentUpdate(
          updatedInstallments,
          installmentNumber,
          {
            dueDate: formatDatePayload(values.dueDate),
            value: Number(values.value ?? 0),
            statusFinancial,
            financialStatus: formatFinancialStatusValue(statusFinancial),
            settlementDate: statusFinancial === "pago" ? settlementDate : null,
            dataBaixa: statusFinancial === "pago" ? settlementDate : null,
            ...(uploadedAttachment
              ? { boletoAttachment: uploadedAttachment }
              : {}),
          },
        ),
        ...(uploadedAttachment ? { boletoAttachment: uploadedAttachment } : {}),
        freightValue: record?.freight?.valueFreightage,
      };

      await freightFinancialService.saveFinancialRecord(freightId, payload);
      message.success("Registro financeiro atualizado com sucesso.");
      resetState();
      onSaved?.();
      onClose?.();
    } catch (error) {
      if (error?.errorFields) {
        const firstField = error.errorFields[0]?.name;
        if (firstField) {
          form.scrollToField(firstField, { block: "center" });
        }
        message.warning("Preencha os campos obrigatórios destacados.");
        return;
      }
      console.error("Erro ao salvar registro financeiro:", error);
      message.error(
        error?.message || "Não foi possível salvar o registro financeiro.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSendBoleto = async () => {
    if (!sendBoletoState.enabled || sendingBoleto) return;

    try {
      setSendingBoleto(true);

      const result = await sendFreightInvoiceBoletoFromPanel({
        freightId,
        freightData,
        record,
        shipperData,
        user,
        boletoFile,
        boletoAttachment,
        financialInstallments,
        installmentNumber: record?.installmentNumber ?? 1,
        resolveUploadedBoletoAttachment: getUploadedBoletoAttachment,
      });

      if (!result?.success) {
        message.warning(result?.message || "Não foi possível enviar o boleto.");
        return;
      }

      message.success(result?.message || "Boleto enviado com sucesso.");
      await loadModalData();
      onSaved?.();
    } catch (error) {
      console.error("Erro ao enviar boleto:", error);
      message.error(error?.message || "Não foi possível enviar o boleto.");
    } finally {
      setSendingBoleto(false);
    }
  };

  const handleCharge = async () => {
    if (!freightId || charging) return;

    try {
      setCharging(true);

      const result = await sendFreightInvoiceChargeFromPanel({
        freightId,
        freightData,
        record,
        shipperData,
        user,
      });

      if (!result?.success) {
        message.warning(
          result?.message || "Não foi possível efetuar a cobrança.",
        );
        return;
      }

      message.success(result?.message || "Cobrança disparada com sucesso.");
    } catch (error) {
      console.error("Erro ao efetuar cobrança:", error);
      message.error(error?.message || "Não foi possível efetuar a cobrança.");
    } finally {
      setCharging(false);
    }
  };

  const boletoStatusLabel = formatFinancialStatusValue(currentFinancialStatus);
  const canRemoveBoleto =
    canManageBoleto &&
    !loading &&
    !saving &&
    !sendingBoleto &&
    !charging &&
    !isFinancialEditLocked;

  return (
    <ConfigProvider theme={antdModalTheme}>
      <Modal
        className="financial-modal-shell"
        title={
          <ModalHeader
            installmentLabel={installmentLabel}
            numberSerial={readOnlyData.numberSerial}
            shipperName={readOnlyData.shipperName}
            readOnly={!canEditFinancial}
          />
        }
        open={open}
        onCancel={handleAttemptClose}
        width={1320}
        centered
        destroyOnClose
        closable={!isBusy}
        maskClosable={!isBusy}
        confirmLoading={loading}
        footer={
          <div className="financial-modal-footer">
            <Button size="large" onClick={handleAttemptClose} disabled={isBusy}>
              {canEditFinancial ? "Cancelar" : "Fechar"}
            </Button>
            <div className="financial-modal-footer__actions">
              {canManageBoleto ? (
                <>
                  <Button
                    size="large"
                    className="financial-modal-footer__charge"
                    onClick={handleCharge}
                    loading={charging}
                    disabled={boletoActionsDisabled}
                  >
                    Efetuar Cobrança
                  </Button>
                  <Tooltip
                    title={
                      sendBoletoState.enabled
                        ? "Enviar o boleto por e-mail aos contatos financeiros do embarcador"
                        : sendBoletoState.reason
                    }
                  >
                    <span>
                      <Button
                        size="large"
                        onClick={handleSendBoleto}
                        disabled={!sendBoletoState.enabled}
                        loading={sendingBoleto}
                      >
                        Enviar Boleto
                      </Button>
                    </span>
                  </Tooltip>
                </>
              ) : null}
              {canEditFinancial ? (
                <Tooltip
                  title={
                    saveButtonState.enabled
                      ? "Salvar alterações do registro financeiro"
                      : saveButtonState.reason
                  }
                >
                  <span>
                    <Button
                      size="large"
                      type="primary"
                      className="financial-modal-footer__save"
                      onClick={handleSave}
                      loading={saving}
                      disabled={!saveButtonState.enabled}
                    >
                      Salvar
                    </Button>
                  </span>
                </Tooltip>
              ) : null}
            </div>
          </div>
        }
        styles={modalStyles}
      >
        <Form
          form={form}
          layout="vertical"
          disabled={financialFieldsDisabled}
          requiredMark="optional"
        >
          <div className="financial-modal-body">
            <div className="financial-modal-grid financial-modal-grid--top">
              <SectionCard icon={<TruckOutlined />} title="Dados do Frete">
                <div className="financial-highlight-grid">
                  <HighlightField
                    label="Código do Frete"
                    value={readOnlyData.numberSerial}
                    emphasis
                  />
                  <HighlightField
                    label="Embarcador"
                    value={readOnlyData.shipperName}
                    emphasis
                  />
                </div>
              </SectionCard>

              <SectionCard
                icon={<DollarCircleOutlined />}
                title="Financeiro Embarcador"
              >
                <HighlightField
                  label="Condições de Pagamento"
                  value={readOnlyData.paymentConditions}
                  emphasis
                  multiline
                />
                <div className="financial-observations-grid">
                  <div>
                    <Text
                      style={{
                        display: "block",
                        marginBottom: 4,
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        color: theme.colors.textSecondary,
                        textTransform: "uppercase",
                      }}
                    >
                      Observações do Cliente
                    </Text>
                    <Input.TextArea
                      value={readOnlyData.shipperObservation}
                      readOnly
                      autoSize={{ minRows: 2, maxRows: 3 }}
                      style={controlStyle}
                    />
                  </div>
                  <div>
                    <Text
                      style={{
                        display: "block",
                        marginBottom: 4,
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        color: theme.colors.textSecondary,
                        textTransform: "uppercase",
                      }}
                    >
                      Observações Internas do Frete
                    </Text>
                    <Input.TextArea
                      value={readOnlyData.internalObservations}
                      readOnly
                      autoSize={{ minRows: 2, maxRows: 3 }}
                      style={controlStyle}
                    />
                  </div>
                </div>
              </SectionCard>
            </div>

            <div className="financial-modal-grid financial-modal-grid--bottom">
              <SectionCard icon={<BarChartOutlined />} title="Dados Financeiros">
                {isFinancialEditLocked ? (
                  <Alert
                    className="financial-locked-banner"
                    type="warning"
                    showIcon
                    message={FINANCIAL_EDIT_LOCKED_MESSAGE}
                  />
                ) : null}

                <div className="financial-fields-grid">
                  <Form.Item
                    label="Status"
                    name="financialStatus"
                    rules={[{ required: true, message: "Selecione o status." }]}
                    required
                  >
                    <Select
                      size="large"
                      options={FINANCIAL_STATUS_OPTIONS}
                      placeholder="Selecione"
                      style={controlStyle}
                    />
                  </Form.Item>

                  <Form.Item
                    label="Data de Carregamento"
                    name="loadingDate"
                    rules={[
                      {
                        required: true,
                        message: "Informe a data de carregamento.",
                      },
                      {
                        validator: (_, value) => {
                          if (!value) return Promise.resolve();
                          if (dayjs(value).isAfter(dayjs(), "day")) {
                            return Promise.reject(
                              new Error("A data não pode ser futura."),
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                    required
                  >
                    <DatePicker
                      size="large"
                      format="DD/MM/YYYY"
                      placeholder="Selecionar data"
                      style={controlStyle}
                      disabledDate={(current) =>
                        current && current.isAfter(dayjs(), "day")
                      }
                    />
                  </Form.Item>

                  <Form.Item
                    label="Data de Vencimento"
                    name="dueDate"
                    rules={[
                      {
                        required: true,
                        message: "Informe a data de vencimento.",
                      },
                    ]}
                    required
                    extra={
                      dueDateOptions.length > 1 ? (
                        <Text
                          type="secondary"
                          className="financial-due-date-hint"
                        >
                          Vencimentos disponíveis:{" "}
                          {dueDateOptions
                            .map((option) => option.label)
                            .join(" · ")}
                        </Text>
                      ) : null
                    }
                  >
                    <DatePicker
                      size="large"
                      format="DD/MM/YYYY"
                      placeholder="Selecionar data"
                      style={controlStyle}
                    />
                  </Form.Item>

                  <Form.Item
                    label="Valor"
                    name="value"
                    rules={[
                      { required: true, message: "Informe o valor." },
                      {
                        validator: (_, value) => {
                          if (Number(value ?? 0) > 0) return Promise.resolve();
                          return Promise.reject(
                            new Error("O valor deve ser maior que zero."),
                          );
                        },
                      },
                    ]}
                    required
                  >
                    <IntlCurrencyInput
                      currency="BRL"
                      config={currencyConfig}
                      className="form-control"
                      disabled={financialFieldsDisabled}
                      style={
                        financialFieldsDisabled
                          ? currencyInputDisabledStyle
                          : currencyInputStyle
                      }
                      value={valueWatch ?? 0}
                      onChange={(_, value) => {
                        if (financialFieldsDisabled) return;
                        form.setFieldsValue({ value });
                      }}
                    />
                  </Form.Item>

                  <Form.Item
                    label="Data da Baixa"
                    name="settlementDate"
                    required={isPaid}
                    rules={[
                      {
                        validator: (_, value) => {
                          if (!isPaid) return Promise.resolve();
                          if (value) return Promise.resolve();
                          return Promise.reject(
                            new Error("Informe a data de baixa."),
                          );
                        },
                      },
                    ]}
                  >
                    <DatePicker
                      size="large"
                      format="DD/MM/YYYY"
                      placeholder="Selecionar data"
                      style={controlStyle}
                      disabled={!isPaid || financialFieldsDisabled}
                    />
                  </Form.Item>
                </div>
              </SectionCard>

              <SectionCard icon={<FileTextOutlined />} title="Boleto Bancário">
                {!canManageBoleto ? (
                  <Text type="secondary">
                    Você não possui permissão para gerenciar boletos desta
                    fatura.
                  </Text>
                ) : hasBoletoAttached ? (
                  <BoletoAttachmentBlock
                    attachment={boletoAttachment}
                    localFile={boletoFile}
                    statusLabel={boletoStatusLabel}
                    canRemove={canRemoveBoleto}
                    removing={removingBoleto}
                    onView={handleViewBoleto}
                    onDownload={handleDownloadBoleto}
                    onRemove={handleRemoveBoleto}
                  />
                ) : (
                  <>
                    <Dragger
                      className="financial-boleto-upload"
                      accept=".pdf,.jpg,.jpeg,.png"
                      maxCount={1}
                      showUploadList={false}
                      beforeUpload={handleBoletoSelect}
                      disabled={financialFieldsDisabled || !canManageBoleto}
                    >
                      <p className="ant-upload-drag-icon" style={{ margin: 0 }}>
                        <InboxOutlined
                          style={{
                            color: theme.colors.primary,
                            fontSize: 28,
                          }}
                        />
                      </p>
                      <p className="ant-upload-text">
                        Clique ou arraste o arquivo para anexar
                      </p>
                      <p className="ant-upload-hint">
                        PDF, JPG ou PNG — até 5MB
                      </p>
                    </Dragger>
                    {isFinancialEditLocked ? (
                      <Text type="secondary" style={{ fontSize: "0.75rem" }}>
                        Upload indisponível: {FINANCIAL_EDIT_LOCKED_MESSAGE}
                      </Text>
                    ) : null}
                  </>
                )}
              </SectionCard>
            </div>
          </div>
        </Form>
      </Modal>
    </ConfigProvider>
  );
}

export default FreightFinancialEditModal;
